/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useMemo} from 'react'
import {Stack, Accordion} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useHistory} from 'react-router-dom'
import {useSortUrls} from '../../../hooks'
import ChipRefinements from './chip-refinements'
import ColorRefinements from './color-refinements'
import RadioRefinements from './radio-refinements'
import {isServer} from '../../../utils/utils'
import {FILTER_ACCORDION_SATE} from '../../../constants'
import PriceRefinements from './price-refinements'
import {trackEvent} from '../../../tracking/analytics'
import FilterAccordionItem from '../../../components/filter-sort/filter-accordion-item'

const componentMap = {
    c_refinementColor: ColorRefinements,
    price: PriceRefinements
}

const Refinements = ({
    basePath,
    filters,
    productSearchResult,
    toggleFilter,
    selectedFilters,
    isLoading,
    searchQuery
}) => {
    const {formatMessage} = useIntl()
    const history = useHistory()
    // use default hardcoded sort by object to align accordion indexes correctly
    filters = useMemo(() => [{attributeId: 'sort_by'}, ...(filters || [])], [filters])

    // Getting the indices of filters to open accordions by default
    let filtersIndexes = filters?.map((_filter, idx) => {
        if (_filter.attributeId === 'c_refinementColor') {
            return idx
        }
    })

    // Use saved state for accordions
    if (!isServer) {
        const savedExpandedAccordionIndexes =
            window.localStorage.getItem(FILTER_ACCORDION_SATE) &&
            JSON.parse(window.localStorage.getItem(FILTER_ACCORDION_SATE))

        if (savedExpandedAccordionIndexes) {
            filtersIndexes = filters
                ?.map((filter, index) => {
                    if (savedExpandedAccordionIndexes.includes(filter.attributeId)) {
                        return index
                    }
                })
                .filter((index) => index !== undefined)
        }
    }

    // Handle saving acccordion state
    const updateAccordionState = (expandedIndex) => {
        const filterState = filters
            ?.filter((_filter, index) => expandedIndex.includes(index))
            .map((filter) => filter.attributeId)
        window.localStorage.setItem(FILTER_ACCORDION_SATE, JSON.stringify(filterState))
    }

    const {sortingOptions} = productSearchResult || {}
    const sortUrls = useSortUrls({options: sortingOptions})
    const selectedSortOptionIndex = useMemo(
        () =>
            Math.max(
                0,
                sortUrls.findIndex((url) => url === basePath.replace(/(offset)=(\d+)/i, '$1=0'))
            ),
        [sortUrls, basePath]
    )
    const sortOption = productSearchResult?.sortingOptions?.[selectedSortOptionIndex]?.label
    const sortingRefinement = useMemo(
        () => ({
            filter: {
                values: sortUrls.map((url) => ({
                    hitCount: 1,
                    value: url,
                    label: productSearchResult?.sortingOptions[sortUrls.findIndex((u) => u === url)]
                        ?.label
                }))
            },
            selectedFilters: [sortUrls[selectedSortOptionIndex]],
            toggleFilter: ({value: url, label}, _, selected) => {
                if (!selected) {
                    history.push(url)

                    trackEvent({
                        event: 'sort_by',
                        content_type: searchQuery ? 'search' : 'PLP',
                        filter_sort_by: label
                    })
                }
            }
        }),
        [sortUrls, selectedSortOptionIndex, searchQuery, history, productSearchResult]
    )

    return (
        <Stack spacing={8}>
            {/* Wait to have filters before rendering the Accordion to allow the deafult indexes to be accurate */}
            {filtersIndexes && (
                <Accordion
                    variant="refinements"
                    pointerEvents={isLoading ? 'none' : 'auto'}
                    onChange={updateAccordionState}
                    opacity={isLoading ? 0.2 : 1}
                    allowMultiple
                    defaultIndex={filtersIndexes}
                    reduceMotion
                >
                    {filters?.map((filter, index) => {
                        if (index === 0) {
                            return (
                                <FilterAccordionItem
                                    key={filter.attributeId}
                                    accordionItemProps={{display: {base: 'flex', lg: 'none'}}}
                                    title={
                                        sortOption
                                            ? formatMessage(
                                                  {
                                                      defaultMessage: 'Sort By: {sortOption}',
                                                      id: 'product_list.select.sort_by'
                                                  },
                                                  {sortOption}
                                              )
                                            : formatMessage({
                                                  defaultMessage: 'Sort By',
                                                  id: 'product_list.modal.accordion.title.sort_by'
                                              })
                                    }
                                >
                                    <RadioRefinements {...sortingRefinement} />
                                </FilterAccordionItem>
                            )
                        } else {
                            // Render the appropriate component for the refinement type, fallback to chips
                            const Values = componentMap[filter.attributeId] || ChipRefinements
                            let selectedFiltersArray = selectedFilters?.[filter.attributeId] ?? []

                            // Ensure that the value is still converted into an array if it's a `string` or `number`.
                            if (typeof selectedFiltersArray === 'string') {
                                selectedFiltersArray = selectedFiltersArray.split('|')
                            } else if (typeof selectedFiltersArray === 'number') {
                                selectedFiltersArray = [selectedFiltersArray]
                            }

                            if (filter.values) {
                                return (
                                    <FilterAccordionItem
                                        key={filter.attributeId}
                                        count={selectedFiltersArray?.length}
                                        label={filter.label}
                                        title={filter.attributeId === 'price' ? filter.label : null}
                                        accordionItemProps={{
                                            display: 'flex'
                                        }}
                                    >
                                        <Values
                                            selectedFilters={selectedFiltersArray}
                                            filter={filter}
                                            toggleFilter={toggleFilter}
                                        />
                                    </FilterAccordionItem>
                                )
                            } else {
                                return null
                            }
                        }
                    })}
                </Accordion>
            )}
        </Stack>
    )
}

Refinements.propTypes = {
    basePath: PropTypes.string,
    filters: PropTypes.array,
    productSearchResult: PropTypes.object,
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.object,
    isLoading: PropTypes.bool,
    searchQuery: PropTypes.string
}

export default Refinements
